import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Check, Sparkles, MousePointer2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Modern spotlight-style guided tour.
 *
 * How the spotlight works:
 *   - A full-screen SVG covers the viewport with a semi-transparent dark rect.
 *   - A <mask> punches a rounded rectangle over the target element (measured
 *     via getBoundingClientRect + ResizeObserver + scroll listeners).
 *   - The spotlight animates smoothly between steps via framer-motion layout.
 *
 * A small animated arrow connects the tooltip card to the spotlighted element.
 * Card position is clamped inside the viewport so it never overflows on mobile.
 *
 * The main tour intentionally emphasises the "vacature → motivatiebrief"
 * shortcut, which is the highest-leverage flow in the app.
 */
export default function OnboardingTour({ onGoto, onClose, variant = "main" }) {
  const { t } = useT();
  const { checkAuth } = useAuth();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const [viewport, setViewport] = useState(() =>
    typeof window !== "undefined"
      ? { w: window.innerWidth, h: window.innerHeight }
      : { w: 1200, h: 800 }
  );
  const cardRef = useRef(null);

  // ---------- Steps ----------
  // Note: `targetTestId` resolves first; if missing on screen we try
  // `fallbackTestId`; if still missing the card centers with no spotlight.
  const MAIN_STEPS = [
    { view: "overview", title: t("obStep1Title"), body: t("obStep1Body") },
    { targetTestId: "nav-profile", view: "profile", title: t("obStep2Title"), body: t("obStep2Body") },
    { targetTestId: "nav-cvs", view: "cvs", title: t("obStep5Title"), body: t("obStep5Body") },
    { targetTestId: "nav-jobsearch", view: "jobsearch", title: t("obStepJobSearchTitle"), body: t("obStepJobSearchBody") },
    {
      // HERO: "Vacature → Motivatiebrief" shortcut on job cards.
      targetTestId: "jobsearch-letter-0",
      fallbackTestId: "jobsearch-form",
      view: "jobsearch",
      title: t("obStepJobToLetterTitle"),
      body: t("obStepJobToLetterBody"),
      accent: true,
    },
    { targetTestId: "nav-letters", view: "letters", title: t("obStep7Title"), body: t("obStep7Body") },
    { view: "letters", title: t("obStepMatchTitle"), body: t("obStepMatchBody") },
    { targetTestId: "nav-overview", view: "overview", title: t("obStep8Title"), body: t("obStep8Body") },
    { view: "overview", title: t("obStepPrepTitle"), body: t("obStepPrepBody") },
    { view: "overview", title: t("obStep9Title"), body: t("obStep9Body") },
  ];

  const PARTNER_STEPS = [
    { view: "profile", title: t("obPartner1Title"), body: t("obPartner1Body") },
    { targetTestId: "affiliate-ref-url", view: "profile", title: t("obPartner2Title"), body: t("obPartner2Body") },
    { targetTestId: "affiliate-open-share-modal", view: "profile", title: t("obPartner3Title"), body: t("obPartner3Body") },
    { targetTestId: "affiliate-copy-banner", view: "profile", title: t("obPartner4Title"), body: t("obPartner4Body") },
    { targetTestId: "affiliate-logo-uploader", view: "profile", title: t("obPartner5Title"), body: t("obPartner5Body") },
    { view: "profile", title: t("obPartner6Title"), body: t("obPartner6Body") },
  ];

  const STEPS = variant === "partner" ? PARTNER_STEPS : MAIN_STEPS;
  const s = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;

  // ---------- Analytics ----------
  // Fire lightweight `view` event on every step change and `skip`/`complete`
  // on close. Failures are ignored — analytics must never block the UI.
  const logEvent = (event, idx = step) => {
    const cur = STEPS[idx];
    if (!cur) return;
    api.post("/onboarding/event", {
      variant: variant === "partner" ? "partner" : "main",
      step_index: idx,
      step_title: cur.title || `Step ${idx + 1}`,
      event,
      total_steps: STEPS.length,
    }).catch(() => { /* ignore */ });
  };

  useEffect(() => {
    logEvent("view");
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- Effects ----------
  // 1) Switch dashboard view when a step requires it.
  useEffect(() => {
    if (s.view && onGoto) onGoto(s.view);
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  // 2) Track viewport (for responsive card positioning).
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // 3) Keyboard navigation.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") finish(true);
      else if (e.key === "ArrowRight" || e.key === "Enter") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line

  // 4) Measure current target (with retries — sidebar swap may take a tick).
  useLayoutEffect(() => {
    setRect(null);
    if (!s.targetTestId && !s.fallbackTestId) return;
    let cancel = false;
    let observer = null;

    const findEl = () =>
      (s.targetTestId && document.querySelector(`[data-testid="${s.targetTestId}"]`)) ||
      (s.fallbackTestId && document.querySelector(`[data-testid="${s.fallbackTestId}"]`));

    const measure = () => {
      const el = findEl();
      if (!el) return false;
      const r = el.getBoundingClientRect();
      if (r.width < 2 || r.height < 2) return false;
      if (!cancel) setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      if (!observer && "ResizeObserver" in window) {
        observer = new ResizeObserver(() => {
          const rr = el.getBoundingClientRect();
          if (!cancel) setRect({ top: rr.top, left: rr.left, width: rr.width, height: rr.height });
        });
        observer.observe(el);
      }
      return true;
    };

    if (!measure()) {
      const timers = [50, 150, 350, 700, 1200].map((ms) => setTimeout(measure, ms));
      const onScroll = () => measure();
      window.addEventListener("scroll", onScroll, true);
      window.addEventListener("resize", onScroll);
      return () => {
        cancel = true;
        timers.forEach(clearTimeout);
        window.removeEventListener("scroll", onScroll, true);
        window.removeEventListener("resize", onScroll);
        observer?.disconnect();
      };
    }
    const onScroll = () => measure();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      cancel = true;
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
      observer?.disconnect();
    };
  }, [step, s.targetTestId, s.fallbackTestId]);

  // ---------- Actions ----------
  const finish = async (skipped = false) => {
    logEvent(skipped ? "skip" : "complete");
    if (variant === "main") {
      try { await api.patch("/auth/preferences", { onboarding_completed: true }); } catch { /* ignore */ }
      try { await checkAuth(); } catch { /* ignore */ }
    }
    onClose?.(skipped);
  };
  const next = () => { logEvent("next"); if (isLast) finish(false); else setStep(step + 1); };
  const prev = () => { if (!isFirst) { logEvent("prev"); setStep(step - 1); } };

  // ---------- Geometry ----------
  const PAD = 10;
  const spot = rect
    ? {
        x: Math.max(0, rect.left - PAD),
        y: Math.max(0, rect.top - PAD),
        w: rect.width + PAD * 2,
        h: rect.height + PAD * 2,
      }
    : null;

  // Card sizing: 448 max, but capped to viewport - 32.
  const CARD_W = Math.min(448, viewport.w - 32);
  // Approximate card height for placement clamp; refined via ref after mount.
  const CARD_H_APPROX = 320;

  // Preferred placement: to the right of the spot; fallback: below or above.
  // For mobile (narrow viewport), always stack bottom of viewport.
  let cardPos;
  let arrowSide = "left"; // side of the card the arrow points from
  if (!spot) {
    cardPos = { top: viewport.h / 2 - CARD_H_APPROX / 2, left: viewport.w / 2 - CARD_W / 2 };
    arrowSide = null;
  } else if (viewport.w < 768) {
    // Mobile: bottom-sheet style card.
    const top = viewport.h - CARD_H_APPROX - 24;
    cardPos = { top: Math.max(16, top), left: Math.max(16, (viewport.w - CARD_W) / 2) };
    arrowSide = spot.y + spot.h / 2 < cardPos.top ? "top" : null;
  } else {
    // Desktop: try right of spot.
    const rightSpace = viewport.w - (spot.x + spot.w) - 24;
    const leftSpace = spot.x - 24;
    const belowSpace = viewport.h - (spot.y + spot.h) - 24;
    if (rightSpace >= CARD_W + 16) {
      cardPos = {
        top: Math.max(16, Math.min(viewport.h - CARD_H_APPROX - 16, spot.y - 8)),
        left: spot.x + spot.w + 24,
      };
      arrowSide = "left";
    } else if (leftSpace >= CARD_W + 16) {
      cardPos = {
        top: Math.max(16, Math.min(viewport.h - CARD_H_APPROX - 16, spot.y - 8)),
        left: spot.x - CARD_W - 24,
      };
      arrowSide = "right";
    } else if (belowSpace >= CARD_H_APPROX + 16) {
      cardPos = {
        top: spot.y + spot.h + 24,
        left: Math.max(16, Math.min(viewport.w - CARD_W - 16, spot.x + spot.w / 2 - CARD_W / 2)),
      };
      arrowSide = "top";
    } else {
      cardPos = {
        top: Math.max(16, spot.y - CARD_H_APPROX - 24),
        left: Math.max(16, Math.min(viewport.w - CARD_W - 16, spot.x + spot.w / 2 - CARD_W / 2)),
      };
      arrowSide = "bottom";
    }
  }

  return (
    <div className="fixed inset-0 z-[70]" data-testid="onboarding-tour">
      {/* SVG spotlight overlay */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-auto"
        onClick={() => finish(true)}
        aria-hidden="true"
      >
        <defs>
          <mask id="onb-spot-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {spot && (
              <motion.rect
                initial={false}
                animate={{ x: spot.x, y: spot.y, width: spot.w, height: spot.h }}
                transition={{ type: "spring", stiffness: 260, damping: 30 }}
                rx="10"
                ry="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(15,15,15,0.62)"
          mask="url(#onb-spot-mask)"
        />
      </svg>

      {/* Animated glow ring around the spot */}
      {spot && (
        <motion.div
          className="absolute pointer-events-none"
          initial={false}
          animate={{ top: spot.y - 2, left: spot.x - 2, width: spot.w + 4, height: spot.h + 4 }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
          data-testid="onboarding-spot"
        >
          <div
            className="w-full h-full rounded-[12px]"
            style={{
              boxShadow: "0 0 0 2px rgba(255,214,102,0.95), 0 0 24px 6px rgba(255,214,102,0.35)",
              animation: "obGlow 2.1s ease-in-out infinite",
            }}
          />
        </motion.div>
      )}

      {/* Small pulsing pointer icon overlaid on target */}
      {spot && s.accent && (
        <motion.div
          className="absolute pointer-events-none text-amber-300"
          initial={false}
          animate={{
            top: spot.y + spot.h - 14,
            left: spot.x + spot.w - 18,
          }}
          transition={{ type: "spring", stiffness: 260, damping: 30 }}
        >
          <motion.div
            animate={{ y: [0, -3, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <MousePointer2 size={22} strokeWidth={2} fill="currentColor" />
          </motion.div>
        </motion.div>
      )}

      {/* Tooltip card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          ref={cardRef}
          data-testid="onboarding-card"
          initial={{ opacity: 0, y: 8, scale: 0.985 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6, scale: 0.99 }}
          transition={{ duration: 0.22, ease: "easeOut" }}
          className="absolute bg-[#F9F8F6] border border-neutral-900 shadow-[0_20px_60px_rgba(0,0,0,0.35)] p-5 sm:p-6"
          style={{
            top: cardPos.top,
            left: cardPos.left,
            width: CARD_W,
          }}
        >
          {/* Arrow */}
          {arrowSide && spot && (
            <div
              aria-hidden
              className="absolute w-3 h-3 bg-[#F9F8F6] border-neutral-900 rotate-45"
              style={
                arrowSide === "left"
                  ? { left: -7, top: Math.min(Math.max(spot.y + spot.h / 2 - cardPos.top - 6, 12), CARD_H_APPROX - 24), borderLeftWidth: 1, borderBottomWidth: 1, borderStyle: "solid" }
                  : arrowSide === "right"
                  ? { right: -7, top: Math.min(Math.max(spot.y + spot.h / 2 - cardPos.top - 6, 12), CARD_H_APPROX - 24), borderRightWidth: 1, borderTopWidth: 1, borderStyle: "solid" }
                  : arrowSide === "top"
                  ? { top: -7, left: Math.min(Math.max(spot.x + spot.w / 2 - cardPos.left - 6, 16), CARD_W - 24), borderLeftWidth: 1, borderTopWidth: 1, borderStyle: "solid" }
                  : { bottom: -7, left: Math.min(Math.max(spot.x + spot.w / 2 - cardPos.left - 6, 16), CARD_W - 24), borderRightWidth: 1, borderBottomWidth: 1, borderStyle: "solid" }
              }
            />
          )}

          {/* Header row: eyebrow + counter + close */}
          <div className="flex items-center justify-between mb-4">
            <span className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-neutral-500 flex items-center gap-2">
              <Sparkles size={11} strokeWidth={1.8} className="text-amber-500" />
              {t("obTourEyebrow")}
              <span className="text-neutral-300">·</span>
              <span data-testid="onboarding-counter">{step + 1} / {STEPS.length}</span>
            </span>
            <button
              data-testid="onboarding-close"
              onClick={() => finish(true)}
              className="text-neutral-500 hover:text-neutral-900"
              title={t("obSkip")}
            >
              <X size={14} strokeWidth={1.7} />
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-1 bg-neutral-200 mb-5 overflow-hidden">
            <motion.div
              className={`h-full ${s.accent ? "bg-amber-500" : "bg-neutral-900"}`}
              initial={false}
              animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              transition={{ type: "spring", stiffness: 220, damping: 26 }}
            />
          </div>

          {/* Title */}
          <h3
            className={`font-serif leading-tight ${s.accent ? "text-2xl sm:text-[1.7rem]" : "text-xl sm:text-[1.35rem]"}`}
          >
            {s.title}
          </h3>
          {s.accent && (
            <p className="mt-2 inline-flex items-center gap-1.5 font-mono text-[0.6rem] uppercase tracking-[0.15em] text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5">
              <Sparkles size={10} strokeWidth={1.8} /> Signature flow
            </p>
          )}

          {/* Body */}
          <p className="text-sm text-neutral-700 leading-relaxed mt-3 mb-6">{s.body}</p>

          {/* Actions */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <button
              data-testid="onboarding-prev"
              onClick={prev}
              disabled={isFirst}
              className="ed-btn ed-btn-outline disabled:opacity-40 !px-3"
            >
              <ChevronLeft size={13} strokeWidth={1.7} /> {t("obPrev")}
            </button>
            <button
              data-testid="onboarding-skip"
              onClick={() => finish(true)}
              className="font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-500 hover:text-neutral-900 underline underline-offset-2 whitespace-nowrap"
            >
              {t("obSkip")}
            </button>
            <button data-testid="onboarding-next" onClick={next} className="ed-btn !px-3">
              {isLast
                ? <><Check size={13} strokeWidth={1.7} /> {t("obFinish")}</>
                : <>{t("obNext")} <ChevronRight size={13} strokeWidth={1.7} /></>}
            </button>
          </div>

          {/* Dots (jump-to) */}
          <div className="flex items-center gap-1.5 mt-5" aria-label={t("obJumpTo")}>
            {STEPS.map((_, i) => (
              <button
                key={i}
                onClick={() => { logEvent("jump", i); setStep(i); }}
                data-testid={`onboarding-dot-${i}`}
                aria-label={`${t("obJumpTo")} ${i + 1}`}
                className={`h-1 rounded-full transition-all ${
                  i === step
                    ? "w-6 bg-neutral-900"
                    : i < step
                    ? "w-3 bg-neutral-500 hover:bg-neutral-700"
                    : "w-3 bg-neutral-300 hover:bg-neutral-500"
                }`}
              />
            ))}
          </div>
        </motion.div>
      </AnimatePresence>

      <style>{`
        @keyframes obGlow {
          0%, 100% { box-shadow: 0 0 0 2px rgba(255,214,102,0.95), 0 0 24px 6px rgba(255,214,102,0.35); }
          50% { box-shadow: 0 0 0 2px rgba(255,214,102,0.55), 0 0 34px 10px rgba(255,214,102,0.15); }
        }
      `}</style>
    </div>
  );
}
